import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth/AuthProvider';
import { supabase } from '../../lib/supabase';
import { useTranslation } from 'react-i18next';
import { useToast } from '@chakra-ui/react';
import { FaGithub, FaGoogle, FaApple, FaEnvelope, FaArrowLeft, FaCheck, FaTrash, FaCopy } from 'react-icons/fa';
import { IOSFormGroup, IOSFormItem, IOSFormInput } from './components/ios/IOSFormGroup';
import { SettingsGroup, SettingsItem } from './components/me';
import { trackEvent } from '../../lib/analytics';
import { getUserType, isLocalUser, getUserDisplayName, getUserDisplayEmail, getUserIdentityProviders } from '../../utils/userUtils';
import { DeleteAccountModal } from '../../components/v3/DeleteAccountModal';
import { SettingsPageLayout } from './components/layout/SettingsPageLayout';

export const AccountManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation(['v3/account', 'v3/common']);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [bindingLoading, setBingingLoading] = useState<string | null>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const userType = getUserType(user);
  const isLocal = isLocalUser(user);
  const displayName = getUserDisplayName(user);
  const displayEmail = getUserDisplayEmail(user);
  const linkedProviders = getUserIdentityProviders(user);

  // 复制用户信息到剪贴板
  const copyUserInfo = async () => {
    if (!user) return;

    const identityInfo = user.identities?.map(identity =>
      `  - ${identity.provider}: ${identity.identity_data?.email || identity.identity_data?.user_name || identity.identity_data?.name || 'N/A'}`
    ).join('\n') || '  None';

    const userInfo = [
      `=== USER INFORMATION ===`,
      `UUID: ${user.id}`,
      `Name: ${displayName}`,
      `Email: ${displayEmail}`,
      `User Type: ${userType}`,
      `Is Local User: ${isLocal}`,
      ``,
      `=== ACCOUNT DETAILS ===`,
      `Created At: ${user.created_at}`,
      `Last Sign In: ${user.last_sign_in_at || 'Never'}`,
      `Email Verified: ${user.email_confirmed_at ? 'Yes' : 'No'}`,
      `Phone: ${user.phone || 'Not set'}`,
      ``,
      `=== LINKED ACCOUNTS ===`,
      identityInfo,
      ``,
      `=== METADATA ===`,
      `Avatar URL: ${user.user_metadata?.avatar_url || 'Not set'}`,
      `Full Name: ${user.user_metadata?.full_name || 'Not set'}`,
      `Display Name: ${user.user_metadata?.display_name || 'Not set'}`,
      `Provider ID: ${user.user_metadata?.provider_id || 'Not set'}`,
      `Username: ${user.user_metadata?.user_name || 'Not set'}`,
      `Preferred Username: ${user.user_metadata?.preferred_username || 'Not set'}`,
      ``,
      `=== RAW DATA ===`,
      `App Metadata: ${JSON.stringify(user.app_metadata, null, 2)}`,
      `User Metadata: ${JSON.stringify(user.user_metadata, null, 2)}`
    ].join('\n');

    try {
      await navigator.clipboard.writeText(userInfo);
      toast({
        title: t('v3/account:success.userInfoCopied'),
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top'
      });
    } catch (err) {
      console.error('Failed to copy user info:', err);
      toast({
        title: t('v3/account:success.userInfoCopyFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    }
  };

  // 验证邮箱表单
  const validateEmailForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!email) {
      newErrors.email = t('v3/account:binding.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('v3/account:binding.invalidEmail');
    }
    
    if (!password) {
      newErrors.password = t('v3/account:binding.passwordRequired');
    }
    
    if (password !== confirmPassword) {
      newErrors.confirmPassword = t('v3/account:binding.passwordMismatch');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 处理社交账号绑定
  const handleSocialBinding = async (provider: 'github' | 'google' | 'apple') => {
    if (!user) return;
    
    setBingingLoading(provider);
    
    try {
      // 记录绑定尝试事件
      await trackEvent('account binding attempt', {
        method: provider,
        user_type: userType
      });

      const { error } = await supabase.auth.linkIdentity({ provider });
      
      if (error) {
        toast({
          title: t('v3/account:binding.bindingFailed'),
          description: error.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top'
        });
        
        // 记录绑定失败事件
        await trackEvent('account binding failed', {
          method: provider,
          user_type: userType,
          error: error.message
        });
      } else {
        toast({
          title: t('v3/account:binding.bindingSuccess'),
          description: isLocal ? t('v3/account:success.accountUpgraded') : t('v3/account:success.accountLinked'),
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top'
        });
        
        // 记录绑定成功事件
        await trackEvent('account binding success', {
          method: provider,
          user_type: userType
        });
      }
    } catch (err) {
      toast({
        title: t('v3/account:errors.unknownError'),
        description: err instanceof Error ? err.message : String(err),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
      
      // 记录绑定异常事件
      await trackEvent('account binding error', {
        method: provider,
        user_type: userType,
        error: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setBingingLoading(null);
    }
  };

  // 处理邮箱绑定
  const handleEmailBinding = async () => {
    if (!user || !validateEmailForm()) return;
    
    setBingingLoading('email');
    
    try {
      // 记录邮箱绑定尝试事件
      await trackEvent('account binding attempt', {
        method: 'email',
        user_type: userType
      });

      // 更新用户邮箱和密码
      const { error } = await supabase.auth.updateUser({
        email,
        password
      });
      
      if (error) {
        if (error.message.includes('email')) {
          toast({
            title: t('v3/account:errors.emailExists'),
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top'
          });
        } else {
          toast({
            title: t('v3/account:binding.bindingFailed'),
            description: error.message,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top'
          });
        }
        
        // 记录绑定失败事件
        await trackEvent('account binding failed', {
          method: 'email',
          user_type: userType,
          error: error.message
        });
      } else {
        toast({
          title: t('v3/account:binding.bindingSuccess'),
          description: isLocal ? t('v3/account:success.accountUpgraded') : t('v3/account:success.accountLinked'),
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top'
        });
        
        // 记录绑定成功事件
        await trackEvent('account binding success', {
          method: 'email',
          user_type: userType
        });
        
        // 重置表单
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setShowEmailForm(false);
      }
    } catch (err) {
      toast({
        title: t('v3/account:errors.unknownError'),
        description: err instanceof Error ? err.message : String(err),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
      
      // 记录绑定异常事件
      await trackEvent('account binding error', {
        method: 'email',
        user_type: userType,
        error: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setBingingLoading(null);
    }
  };

  // 处理删除账号
  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
  };

  return (
    <SettingsPageLayout
      title={isLocal ? t('v3/account:upgrade.title') : t('v3/account:management.title')}
      onBack={() => navigate(-1)}
    >

      {/* 当前账号信息 */}
      <SettingsGroup title={t('v3/account:management.currentAccount')}>
        <div className="p-4 flex items-center">
          <div className="w-12 h-12 rounded-full bg-ios-blue dark:bg-ios-dark-blue flex items-center justify-center text-white text-lg font-semibold">
            {displayName.charAt(0)}
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-ios-headline font-medium">{displayName}</h3>
            <div className="flex items-center gap-2">
              {displayEmail && (
                <p className="text-ios-subhead text-ios-gray dark:text-ios-dark-gray">{displayEmail}</p>
              )}
              <button
                onClick={copyUserInfo}
                className="flex items-center gap-1 px-2 py-1 text-ios-blue dark:text-ios-dark-blue hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray5 rounded-ios-sm transition-colors text-xs"
                title={t('v3/account:management.copyUserInfo')}
              >
                <FaCopy className="w-3 h-3" />
                <span>{t('v3/account:management.copyUserInfo', '复制我的用户信息')}</span>
              </button>
            </div>
          </div>
          {isLocal && (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-ios-orange/20 text-ios-orange">
              {t('v3/account:status.local', '本地')}
            </span>
          )}
        </div>
      </SettingsGroup>

      {/* 升级说明（仅本地用户） */}
      {isLocal && (
        <SettingsGroup>
          <div className="p-4">
            <h3 className="text-ios-headline font-medium mb-2">{t('v3/account:upgrade.description')}</h3>
            <div className="space-y-2">
              <div className="flex items-center text-ios-subhead text-ios-gray dark:text-ios-dark-gray">
                <FaCheck className="w-4 h-4 text-ios-green mr-2" />
                {t('v3/account:upgrade.benefits.cloudSync')}
              </div>
              <div className="flex items-center text-ios-subhead text-ios-gray dark:text-ios-dark-gray">
                <FaCheck className="w-4 h-4 text-ios-green mr-2" />
                {t('v3/account:upgrade.benefits.multiDevice')}
              </div>
              <div className="flex items-center text-ios-subhead text-ios-gray dark:text-ios-dark-gray">
                <FaCheck className="w-4 h-4 text-ios-green mr-2" />
                {t('v3/account:upgrade.benefits.dataBackup')}
              </div>
              <div className="flex items-center text-ios-subhead text-ios-gray dark:text-ios-dark-gray">
                <FaCheck className="w-4 h-4 text-ios-green mr-2" />
                {t('v3/account:upgrade.benefits.moreFeatures')}
              </div>
            </div>
            <p className="mt-3 text-ios-footnote text-ios-blue dark:text-ios-dark-blue">
              {t('v3/account:upgrade.dataWillBePreserved')}
            </p>
          </div>
        </SettingsGroup>
      )}

      {/* 已绑定账号（仅正式用户） */}
      {!isLocal && linkedProviders.length > 0 && (
        <SettingsGroup title={t('v3/account:management.linkedAccounts')}>
          {linkedProviders.map((provider) => (
            <SettingsItem
              key={provider}
              icon={
                provider === 'github' ? <FaGithub className="w-5 h-5" /> :
                provider === 'google' ? <FaGoogle className="w-5 h-5" /> :
                provider === 'apple' ? <FaApple className="w-5 h-5" /> :
                <FaEnvelope className="w-5 h-5" />
              }
              title={
                provider === 'github' ? 'GitHub' :
                provider === 'google' ? 'Google' :
                provider === 'apple' ? 'Apple' :
                'Email'
              }
              iconBgColor="bg-ios-green/10 dark:bg-ios-dark-green/20"
            />
          ))}
        </SettingsGroup>
      )}

      {/* 绑定选项 */}
      <SettingsGroup title={isLocal ? t('v3/account:upgrade.chooseMethod') : t('v3/account:management.bindMoreAccounts')}>
        {/* 社交账号绑定 */}
        {!linkedProviders.includes('github') && (
          <SettingsItem
            icon={<FaGithub className="w-5 h-5 text-ios-gray dark:text-ios-dark-gray" />}
            title={t('v3/account:binding.bindGithub')}
            onClick={() => handleSocialBinding('github')}
            loading={bindingLoading === 'github'}
            iconBgColor="bg-ios-gray/10 dark:bg-ios-dark-gray/20"
          />
        )}
        
        {!linkedProviders.includes('google') && (
          <SettingsItem
            icon={<FaGoogle className="w-5 h-5 text-ios-gray dark:text-ios-dark-gray" />}
            title={t('v3/account:binding.bindGoogle')}
            onClick={() => handleSocialBinding('google')}
            loading={bindingLoading === 'google'}
            iconBgColor="bg-ios-gray/10 dark:bg-ios-dark-gray/20"
          />
        )}
        
        {!linkedProviders.includes('apple') && (
          <SettingsItem
            icon={<FaApple className="w-5 h-5 text-ios-gray dark:text-ios-dark-gray" />}
            title={t('v3/account:binding.bindApple')}
            onClick={() => handleSocialBinding('apple')}
            loading={bindingLoading === 'apple'}
            iconBgColor="bg-ios-gray/10 dark:bg-ios-dark-gray/20"
          />
        )}

        {/* 邮箱绑定 */}
        {(isLocal || !user?.email) && (
          <SettingsItem
            icon={<FaEnvelope className="w-5 h-5 text-ios-gray dark:text-ios-dark-gray" />}
            title={t('v3/account:binding.bindEmail')}
            onClick={() => setShowEmailForm(!showEmailForm)}
            iconBgColor="bg-ios-gray/10 dark:bg-ios-dark-gray/20"
          />
        )}
      </SettingsGroup>

      {/* 邮箱绑定表单 */}
      {showEmailForm && (
        <SettingsGroup>
          <div className="p-4">
            <IOSFormGroup>
              <IOSFormItem>
                <IOSFormInput
                  value={email}
                  onChange={setEmail}
                  placeholder={t('v3/account:binding.emailPlaceholder')}
                  type="email"
                />
                {errors.email && (
                  <p className="mt-1 text-ios-footnote text-ios-red">{errors.email}</p>
                )}
              </IOSFormItem>
              <IOSFormItem>
                <IOSFormInput
                  value={password}
                  onChange={setPassword}
                  placeholder={t('v3/account:binding.passwordPlaceholder')}
                  type="password"
                  showPasswordToggle={true}
                />
                {errors.password && (
                  <p className="mt-1 text-ios-footnote text-ios-red">{errors.password}</p>
                )}
              </IOSFormItem>
              <IOSFormItem>
                <IOSFormInput
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  placeholder={t('v3/account:binding.confirmPassword')}
                  type="password"
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-ios-footnote text-ios-red">{errors.confirmPassword}</p>
                )}
              </IOSFormItem>
            </IOSFormGroup>
            
            <div className="flex space-x-3 mt-4">
              <button
                onClick={() => setShowEmailForm(false)}
                className="flex-1 py-3 border border-ios-gray5 dark:border-ios-dark-gray4 rounded-ios-lg text-ios-gray dark:text-ios-dark-gray font-medium"
              >
                {t('common:actions.cancel', '取消')}
              </button>
              <button
                onClick={handleEmailBinding}
                disabled={bindingLoading === 'email'}
                className="flex-1 py-3 bg-ios-blue dark:bg-ios-dark-blue text-white font-medium rounded-ios-lg disabled:opacity-50"
              >
                {bindingLoading === 'email' ? t('v3/account:binding.bindingInProgress') : t('v3/account:binding.bindEmail')}
              </button>
            </div>
          </div>
        </SettingsGroup>
      )}

      {/* 危险操作区域 */}
      <SettingsGroup title={t('v3/account:dangerZone.title')}>
        <SettingsItem
          icon={<FaTrash className="w-5 h-5 text-ios-red" />}
          title={t('v3/account:dangerZone.deleteAccount.title')}
          description={t('v3/account:dangerZone.deleteAccount.description')}
          onClick={handleDeleteAccount}
          iconBgColor="bg-ios-red/10 dark:bg-ios-red/20"
          showChevron={false}
        />
      </SettingsGroup>

      {/* 删除账号确认弹窗 */}
      {showDeleteModal && (
        <DeleteAccountModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          user={user}
          userType={userType}
          isLocal={isLocal}
          linkedProviders={linkedProviders}
        />
      )}
    </SettingsPageLayout>
  );
};

export default AccountManagement;
